import { isLessonResourceDestination } from "./lessonResourceDestination.js";

/**
 * Where a manual sign-in lands.
 *
 * Signing in is normally a fresh start: the signed-out screen can be drawn at
 * *any* address, because auth is checked before the app routes are rendered, so
 * keeping that address after login used to wake the shell on a route the
 * account cannot reach — the sidebar appears and the main area is a dead end.
 * Returning to the root and letting the authenticated shell choose its own home
 * (Today, Diary, or family Home) avoids that for every stale or private URL.
 *
 * The exceptions are addresses we ourselves send from outside the Hub.
 * Lesson resource destinations survive for staff and families, then the
 * server rechecks access before opening the file. The lesson-block coupon
 * email's call to action is `/book`, and that
 * journey is the whole point of the email: a family following it while signed
 * out must not be dropped on Home to find Book a lesson by themselves.
 * `/setup` likewise continues a confirmed account's purchase or learner setup.
 * `/notes` opens the lesson notes linked from private and School lesson emails.
 *
 * So the allowlist is deliberately tiny, and it is an allowlist rather than
 * "keep whatever was there". Two rules keep the dead end from coming back:
 *
 * - only a destination named here survives a sign-in;
 * - staff never keep `/book`, `/setup` or `/notes`, because these are family
 *   destinations. A staff account opening one returns to its own home rather than to the
 *   "That page does not exist." the staff shell would draw.
 *
 * A family account that *cannot* book is still safe to send: the family router
 * gates `/book` and redirects to Home, which is what a signed-in family with no
 * bookable student sees anyway.
 */
const PRESERVED_SIGNED_OUT_DESTINATIONS: ReadonlySet<string> = new Set(["/book", "/setup", "/notes", "/link-child"]);

/** The app root, which each authenticated shell resolves to its own home. */
const APP_ROOT = "/";

export function postSignInDestination(
  pathname: string,
  { staff }: { staff: boolean },
): string {
  // Both staff and families can open note resources; the server rechecks the
  // precise destination after sign-in. No user-supplied return URL is accepted.
  if (isLessonResourceDestination(pathname)) return pathname;
  if (staff && pathname === "/calday-room/manage") return pathname;
  if (/^\/help(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)?\/?$/.test(pathname)) return pathname;
  if (staff) return APP_ROOT;
  if (/^\/link-child\/confirm\/[A-Za-z0-9_-]{43}$/.test(pathname)) return pathname;

  // A trailing slash is the same destination; anything else is not normalised,
  // so an unexpected shape simply falls back to the root.
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  return PRESERVED_SIGNED_OUT_DESTINATIONS.has(normalized) ? normalized : APP_ROOT;
}
